<template>

    <v-container fluid>
        <v-row dense>
            <v-col cols="12">

                <v-alert class="body-2 mb-2"
                         prominent
                         icon="mdi-alert"
                         v-show="enabledModel && (!selectedSubPanels || selectedSubPanels.length === 0)"
                         type="warning">
                    Segment has not yet been selected. Displaying all sub panels and languages!
                </v-alert>

                <v-scale-transition>
                    <title-card
                        :no-padding="true"
                        :loading="loading"
                        help="edit-project-welcome-settings"
                        title="Settings">

                        <template v-slot:buttons>

                            <v-btn v-show="showRememberButton"
                                   color="primary"
                                   small
                                   @click="rememberLocaleButtonClicked"
                            >
                                Remember locale?
                            </v-btn>
                        </template>

                        <v-container fluid class="pt-2 pl-5 pr-5 pb-0">
                            <v-row dense>

                                <v-col cols=4>

                                    <v-select
                                        v-model="enabledModel"
                                        :items="enabledItems"
                                        item-text="name"
                                        item-value="value"
                                        label="Show welcome page"
                                    ></v-select>
                                </v-col>

                                <v-col cols=4>
                                    <v-select
                                        v-model="subPanelModel"
                                        :items="availableSubPanels"
                                        item-text="name"
                                        item-value="type"
                                        return-object
                                        label="Preview welcome page for"
                                        @change="subPanelChanged"
                                    ></v-select>
                                </v-col>

                                <v-col cols=4 v-if="subPanelModel">
                                    <v-select
                                        v-model="localeModel"
                                        :items="locales"
                                        item-text="code"
                                        item-value="code"
                                        return-object
                                        label="Preview for locale"
                                        @change="localeChanged"
                                    ></v-select>
                                </v-col>

                            </v-row>
                        </v-container>

                    </title-card>
                </v-scale-transition>
            </v-col>

            <v-col cols="12">
                <v-scale-transition>
                    <title-card v-show="!loading && enabledModel"
                                :no-padding="true"
                                help="landing-page-editor"
                                title="Welcome page editor">

                        <v-scale-transition>

                            <landing-page
                                v-if="!loading && translations"
                                class="pt-10 pb-10"
                                type="project-entry"
                                :logo="subPanelModel.logo_url"
                                :edit-mode="false"
                                :edit-content-only="true"
                                :sub-panel-id="subPanelModel.id"
                                :sub-panel-name="subPanelModel.name"
                                :locale="localeModel ? localeModel : ''"
                                :community-url="subPanelModel && subPanelModel.url"
                                :support-email="subPanelModel && subPanelModel.support_email"
                                :translations="translations"
                                :rewards-earned="0"
                                :component-data="componentData"
                                :settings="subPanelModel.landing_page_settings"
                                :sub-panel-translations="subPanelTranslations"
                                @change:translations="updateLandingPageTranslations"
                            ></landing-page>
                        </v-scale-transition>

                    </title-card>
                </v-scale-transition>

            </v-col>
        </v-row>

        <json v-if="false" :data="data"></json>

    </v-container>

</template>

<script>

import TitleCard from "../../../../../components/UI/TitleCard";
import {mapState, mapActions} from 'vuex';
import EmailTheme from "../../../../../components/UI/EmailTheme";
import Json from 'vue-json-pretty';
import LandingPage from "../../../../../components/UI/LandingPage";
import {preferredLocale} from "../../../../../components/mixins/preferredLocaleMixin";
import merge from "deepmerge";

export default {

    mixins: [preferredLocale],

    components: {
        EmailTheme,
        Json,
        TitleCard,
        LandingPage
    },

    props: {
        project: {
            type: Object,
            required: true,
        },
        selectedSubPanels: {
            type: Array,
            required: true,
        }
    },

    data() {
        return {
            subPanelModel: null,
            localeModel: null,
            data: null,
            loading: false,
            sending: false,
            menuModel: null,
            initialEmit: false,
            enabledItems: [
                {name: 'Enabled', value: true},
                {name: 'Disabled', value: false}
            ],
            templateLoaded: false,
            subPanelTranslationsLibrary: null,
            showRememberButton: false,
        }
    },

    computed: {

        ...mapState('SubPanels', [
            'subPanels',
            'landingPageTemplates'
        ]),

        enabledModel: {
            get() {
                if (this.project) {
                    return this.project.welcome_enabled;
                }
                return false;
            },
            set(value) {
                this.$emit('change', {
                    welcome_enabled: value
                });

                if (value && !this.data) {
                    this.fetchLandingPages();
                }
            }
        },

        componentData() {
            return {
                projectInfo: {
                    welcome_enabled: true,
                    loi: this.project.loi,
                    reward_completed: this.project.reward_completed,
                    mobile_friendly: this.project.mobile_friendly,
                    table_friendly: this.project.table_friendly,
                    desktop_friendly: this.project.desktop_friendly
                }
            }
        },

        locales() {
            if (this.subPanelModel) {
                return this.subPanelModel.locales;
            }
        },

        translations() {

            if (this.data && this.localeModel && this.subPanelModel) {
                // Return default translations
                const library = this.data.find(subPanel => subPanel.id === this.subPanelModel.id);
                return library.translations[this.localeModel.code];
            }
        },

        subPanelTranslations() {
            if (this.data && this.localeModel && this.subPanelModel) {
                const subPanel = this.subPanelTranslationsLibrary.find(subPanel => subPanel.id === this.subPanelModel.id);
                return subPanel.translations[this.localeModel.code];
            }
        },

        availableSubPanels() {
            if (this.selectedSubPanels.length === 0) {
                return [...this.subPanels];
            } else {
                return this.subPanels.filter(subPanel => this.selectedSubPanels.includes(subPanel.id));
            }
        }
    },

    watch: {
        selectedSubPanels() {
            this.fetchLandingPages();
        }
    },

    mounted() {
        if (this.enabledModel) {
            this.fetchLandingPages();
        }
    },

    methods: {

        ...mapActions('SubPanels', [
            'loadAllLandingPageTemplates',
        ]),

        subPanelChanged() {
            // See if the selected sub panel contains same locale as currently selected
            // if so keep otherwise pick first available locale from the sub panel locales.
            const locale = this.subPanelModel.locales.find(locale => locale.id === this.localeModel.id)
            this.localeModel = locale ? locale : this.findPreferredLocale(this.subPanelModel);
        },

        localeChanged() {
            this.showRememberButton = true;
        },

        rememberLocaleButtonClicked() {
            this.setPreferredLocale(this.localeModel.code);
        },

        mergeTranslations(target) {

            // New project
            if (!this.project.welcome_translations) return target;

            target.forEach(subPanelTemplate => {
                const projectTemplate = this.project.welcome_translations.find(sp => subPanelTemplate.id === sp.id);

                if (projectTemplate) {
                    subPanelTemplate.translations = merge.all([subPanelTemplate.translations, projectTemplate.translations]);
                }
            });
            return target;
        },

        fetchLandingPages() {

            const subPanelIds = this.availableSubPanels.map(subPanel => subPanel.id);

            this.loadAllLandingPageTemplates({
                subPanelIds: subPanelIds,
                type: 'project-entry'
            })
                .then((response) => {
                    this.subPanelModel = this.availableSubPanels[0];
                    this.localeModel = this.findPreferredLocale(this.subPanelModel);

                    this.data = this.mergeTranslations(response.data.landing_page_translations);
                    this.subPanelTranslationsLibrary = response.data.sub_panel_translations;

                    this.emit();
                })
                .catch((error) => {
                    console.log(error);
                    Bus.$emit('flash-message', {type: 'error', message: 'Failed loading email templates'});
                })
                .finally(() => {
                    this.loading = false;
                })
        },

        findTranslations() {
            const library = this.data.find(translation => parseInt(translation.id) === parseInt(this.subPanelModel.id));
            return library.translations[this.localeModel.code];
        },

        updateLandingPageTranslations(data) {
            let translation = this.findTranslations();
            translation.content = data.content;
            this.emit();
        },

        emit() {
            this.$emit('change', {
                welcome_translations: this.data,
            });
        }
    }
}
</script>
